import { useQueryCertificateAll } from "@/api/certificate/certificate";
import { CertificateStatus } from "@/api/enums/CertificateStatus";
import { RenewingType } from "@/api/enums/RenewingType";
import useChildForm from "@/hooks/ChildForm";
import { Perimeter } from "@/lib/perimeter";
import { useEffect, useMemo, useState } from "react";
import { PerimeterForm } from "../../PerimeterForm/PerimeterForm";
import CertificatesSwitches from "../CertificatesSwitches";
import RenewingTypeRadio from "./RenewingTypeRadio";

interface RenewingPropertiesFormProps {
  candidateId?: number;
  fieldName?: string;
}

const RenewingPropertiesForm = ({
  fieldName,
  candidateId,
}: RenewingPropertiesFormProps) => {
  const [renewalAllowedPerimeter, setRenewalAllowedPerimeter] =
    useState<Perimeter>(Perimeter.empty());
  const { transformPath, watch, setValue } = useChildForm(fieldName);

  const certificatesQuery = useQueryCertificateAll(candidateId);

  const renewingType = watch(transformPath("renewingType"));
  const renewingCertificatesIDs = watch(
    transformPath("renewingCertificatesIDs")
  );

  const validCertificates = useMemo(
    () =>
      certificatesQuery.data?.filter((certificate) => {
        const expirationDate = new Date(certificate.expirationDate);
        return (
          (certificate.status as CertificateStatus) ===
            CertificateStatus.VALIDATED && expirationDate >= new Date()
        );
      }) ?? [],
    [certificatesQuery.data]
  );

  // merging perimeters is allowed only if the difference is
  // in merchandises or transport modes, not both
  const renewalAllowedCertificates = useMemo(() => {
    if (
      renewingCertificatesIDs.length === 0 ||
      renewingType !== RenewingType.FUSION
    )
      return validCertificates;

    const selectedCertificates = validCertificates.filter(({ id }) =>
      renewingCertificatesIDs.includes(id)
    );

    if (selectedCertificates.length === 0)
      throw new Error("no certificate found");

    // Filter certificates to only include those that have a non-empty intersection
    // with ALL selected certificates
    return validCertificates.filter(({ id, perimeter }) => {
      // Always include already selected certificates
      if (renewingCertificatesIDs.includes(id)) return true;

      // Check if this certificate has a non-empty intersection with all selected certificates
      return selectedCertificates.every((selectedCert) => {
        const union = Perimeter.union(
          Perimeter.from(perimeter),
          Perimeter.from(selectedCert.perimeter)
        );
        return (
          (union.transportModes.length === perimeter.transportModes.length &&
            union.transportModes.length ===
              selectedCert.perimeter.transportModes.length) ||
          (union.merchandises.length === perimeter.merchandises.length &&
            union.merchandises.length ===
              selectedCert.perimeter.merchandises.length)
        );
      });
    });
  }, [validCertificates, renewingType, renewingCertificatesIDs]);

  // auto-select allowed fields
  useEffect(() => {
    const newAllowedPerimeter = Perimeter.union(
      ...(certificatesQuery.data
        ?.filter(({ id }) => renewingCertificatesIDs.includes(id as number))
        .map(({ perimeter }) => Perimeter.from(perimeter)) || [])
    );

    if ([RenewingType.SAME, RenewingType.FUSION].includes(renewingType)) {
      setValue(transformPath("perimeter"), newAllowedPerimeter);
    } else {
      const restrictedPerimeter = Perimeter.from(
        watch(transformPath("perimeter"))
      ).restrictByOther(newAllowedPerimeter);
      setValue(transformPath("perimeter"), restrictedPerimeter);
    }

    setRenewalAllowedPerimeter(newAllowedPerimeter);
  }, [
    renewingType,
    renewingCertificatesIDs,
    setValue,
    watch,
    certificatesQuery.data,
    transformPath,
  ]);

  // avoid multiple selections if forbidden
  useEffect(() => {
    if ([RenewingType.SAME, RenewingType.REDUCED].includes(renewingType)) {
      setValue(
        transformPath("renewingCertificatesIDs"),
        watch(transformPath("renewingCertificatesIDs")).slice(0, 1)
      );
    }
  }, [renewingType, setValue, watch, transformPath]);

  return (
    <>
      <RenewingTypeRadio fieldName={transformPath("renewingType")} />
      {certificatesQuery.isLoading ? (
        <div>Chargement des certificats...</div>
      ) : certificatesQuery.isError ? (
        <div>Erreur lors du chargement des certificats.</div>
      ) : (
        <>
          <CertificatesSwitches
            certificates={renewalAllowedCertificates}
            unique={[RenewingType.SAME, RenewingType.REDUCED].includes(
              renewingType
            )}
            fieldName={transformPath("renewingCertificatesIDs")}
            titleElement={<h2>Vos certificat(s) à renouveler</h2>}
          />
          <PerimeterForm
            fieldName={transformPath("perimeter")}
            disabledPerimeter={
              [RenewingType.SAME, RenewingType.FUSION].includes(renewingType)
                ? Perimeter.full()
                : Perimeter.empty()
            }
            displayedPerimeter={renewalAllowedPerimeter}
            uncheckedAreRed={renewingType === RenewingType.REDUCED}
          />
        </>
      )}
    </>
  );
};

export default RenewingPropertiesForm;
